import { SafeUrl } from '@angular/platform-browser'

import { User } from '@shared/models/user.model'
import { BaseModel } from '@shared/models/base.model'
import { assign } from 'lodash-es'
import { UploadedFile } from '../uploaded-file'

export enum StateCentrifugeStateEnum {
  Connected = 'connected',
  Disconnected = 'disconnected',
}

export enum StateCentrifugeTypeEnum {
  State = 'state',
  Error = 'error',
}

export interface StateCentrifuge {
  type: StateCentrifugeTypeEnum
  state: StateCentrifugeStateEnum
  data?: unknown
}

export interface LastChatHistoryModel {
  createdAt: string
  file: string | number | null
  id: number | string
  isRead: boolean
  location: unknown | null
  message: string
  user: ChatUserModel
}

export class LastChatHistory implements LastChatHistoryModel {
  createdAt: string
  file: string | number | null
  id: string
  isRead: boolean
  location: unknown | null
  message: string
  user: ChatUserModel

  constructor(props: LastChatHistoryModel) {
    if (props) {
      assign(this, props)
    }
    this.user = new ChatUser(props.user)
  }
}

export interface ChatModel {
  id: number
  name: string
  channel: string
  createdAt: string
  createdBy: ChatUserModel
  updatedAt: string | null
  updatedBy: ChatUserModel | null
  lastSentAt: string
  users: ChatUserModel[]
  lastChatHistory?: LastChatHistoryModel | null
  unreadCount: number
  leader?: User
  isIndividual: boolean
}

export class Chat extends BaseModel implements ChatModel {
  id: number
  name: string
  channel: string
  createdAt: string
  createdBy: ChatUserModel
  updatedAt: string | null
  updatedBy: ChatUserModel | null
  lastSentAt: string
  users: ChatUserModel[]
  lastChatHistory: LastChatHistoryModel
  unreadCount: number
  leader?: User
  isIndividual: boolean

  constructor(props: Partial<ChatModel>) {
    super(props)
    if (props.users) {
      this.users = props.users?.map((user) => new ChatUser(user))
    }

    if (props.updatedBy) {
      this.updatedBy = new ChatUser(props.updatedBy)
    }

    if (props.createdBy) {
      this.createdBy = new ChatUser(props.createdBy)
    }

    if (props.lastChatHistory) {
      this.lastChatHistory = new LastChatHistory(props.lastChatHistory)
    }
  }
}

export interface ChatFormData {
  name?: string
  userIds: (string | number)[]
}

export interface ChatMessageFormData {
  message?: string
  attachment?: Blob
}

export interface ChatMessageDraftData extends ChatMessageFormData {
  picture?: string | ArrayBuffer | SafeUrl
}

export interface ChatUserModelLastOnlineDate {
  date: string
  timezone: string
  timezone_type: number
}

export type ChatUserModel = {
  networkStatus: number
  leader?: boolean
  fullName?: string
  data?: unknown
} & Pick<User, 'id' | 'email' | 'name' | 'surname' | 'role' | 'phone' | 'status' | 'lastLoggedAt' | 'picture'>

export class ChatUser extends User implements ChatUserModel {
  networkStatus: number
  leader?: boolean
  picture: string

  constructor(props: ChatUserModel) {
    delete props.fullName
    super(props)
    this.networkStatus = props.networkStatus
    this.picture = props.picture && this.createPictureUrl(props.picture)
  }

  private createPictureUrl(url: string): string {
    if (url.includes(this.fileUrl)) {
      return url
    }
    return this.fileUrl + url
  }
}

export const DELETED_MESSAGE = 4

export enum ChatMessageModelType {
  Normal = 1,
  System = 2,
}

export interface ChatMessageModel {
  id: number
  chat: Pick<ChatModel, 'id' | 'name' | 'channel' | 'lastSentAt'>
  user: ChatUserModel
  isRead: boolean
  isReadByOther: boolean
  message: string
  createdAt: string
  createdBy: string
  updatedAt: null | string
  updatedBy: null | string
  file: null | UploadedFile
  location?: null | string
  event?: number
  type?: ChatMessageModelType
}

export class ChatMessage extends BaseModel implements ChatMessageModel {
  id: number
  chat: Pick<ChatModel, 'id' | 'name' | 'channel' | 'lastSentAt'>
  user: ChatUserModel
  isRead: boolean
  isReadByOther: boolean
  message: string
  createdAt: string
  createdBy: string
  updatedAt: null | string
  updatedBy: null | string
  file: null | UploadedFile
  location?: null | string
  event?: number
  type?: ChatMessageModelType

  constructor(props: ChatMessageModel) {
    super(props)
    this.user = new ChatUser(props.user)
    this.chat = new Chat(props.chat)
    this.file = props.file && new UploadedFile(props.file)
  }
}

export interface ChatSocketEventData {
  chat?: ChatModel
  message?: ChatMessageModel
  typing?: boolean
}

export interface ChatSocketEvent {
  event: ChatSocketEventEnum
  user: ChatUserModel
  data: ChatSocketEventData | null
  chat?: ChatModel
  message?: ChatMessageModel
}

export interface ChatSocketResponseBase {
  data?: unknown
  gen?: unknown
  offset?: number
  seq?: unknown
}

export interface ChatSocketResponse extends ChatSocketResponseBase {
  data: ChatSocketEvent
  info?: {
    client?: string
    user?: string
  }
}

export interface ChannelData {
  channel: string
}

export enum ChatSocketEventEnum {
  EVENT_CHAT_CREATED = 'chatCreated',
  EVENT_CHAT_EDITED = 'chatEdited',
  EVENT_CHAT_DELETED = 'chatDeleted',
  EVENT_MESSAGE_SENT = 'messageSent',
  EVENT_MESSAGE_READ = 'messageRead',
  EVENT_MESSAGE_EDITED = 'messageEdited',
  EVENT_MESSAGE_DELETED = 'messageDeleted',
  EVENT_USERS_DELETED_FROM_CHAT = 'usersDeletedFromChat',
  EVENT_USERS_ADDED_TO_CHAT = 'usersAddedToChat',
  EVENT_USER_STATUS_UPDATED = 'userStatusUpdated',
  EVENT_MESSAGE_TYPING = 'messageTyping',
}

export interface UserChannelsResponse {
  private: string
  team: string
}

export enum ChatListFilterEnum {
  INDIVIDUAL = 'individual',
  GROUP = 'group',
}

export interface FilterMenuItem {
  value: ChatListFilterEnum
  title: string
}

export interface ChatMessageGroup {
  day: string
  chatMessageList: ChatMessageModel[]
}

export enum ChatMessageOption {
  DELETE = 'delete',
  DELETE_ATTACHMENT = 'deleteAttachment',
  EDIT = 'edit',
}
